(ns hai.home.mobile.mini-apps.notes
  "My Notes mini-app card for the mobile dashboard: the user's latest notes as
   a list ([[hai.notes.recent]]). ＋ swaps the card body for the shared note
   editor on a blank draft and a row swaps it for the editor on that note;
   while it is open the header retitles and holds ➤, ⋮, ⤢ and ✕, with ⤢
   opening the same draft in the full-screen editor. The title links to the
   full Notes page. Everything shown comes from [[hai.notes.mini-app]]; the
   card frame stays mobile's own, with touch-sized targets."
  (:require
   [hai.entity.data :as entity-data]
   [hai.entity.sections :as sections]
   [hai.home.mobile.mini-apps :as mini-apps]
   [hai.mini-apps.add-pill :as add-pill]
   [hai.mini-apps.data :as mini-apps-data]
   [hai.mini-apps.frame :as frame]
   [hai.notes.editor :as editor]
   [hai.notes.header :as header]
   [hai.notes.mini-app :as notes-mini-app]
   [hai.notes.recent :as recent]
   [hai.sharing.helper :as sharing]
   [hai.spaces.data :as spaces-data]
   [hai.ui.rf-sugar :refer [<s >e]]
   [hai.ui.tokens :as t]
   [hai.web-app.routing :as routing]
   [lambdaisland.ornament :as o]
   [reagent.core :as r]))

;; Links the add/save toasts to the note. Home lists notes rather than showing
;; one, so the link always has somewhere to go.
(def ^:private note-link
  {:href-fn #(routing/href :notes/detail {:note/uuid %})
   :target routing/link-target})

;; The frame stays mobile's own — softer shadow, 20px radius, touch-sized
;; targets — but the header row is the shared one every notes surface uses.
(o/defstyled card mini-apps/card
  ;; Collapsed, the bar is the whole card, so it rounds its bottom corners too.
  [:&.collapsed
   [header/bar
    {:border-bottom-left-radius "20px"
     :border-bottom-right-radius "20px"}]]
  [header/bar
   {:border-top-left-radius "20px"
    :border-top-right-radius "20px"}
   ;; Touch targets, as with the mobile frame's own buttons.
   [:.bar-action-btn {:width "32px" :height "32px"}]
   [add-pill/pill
    {:height "38px"}
    [:.add-action {:height "30px"}]
    [:.add-menu {:width "30px" :height "30px"}]]
   ;; The header's ⋮ matches the round header buttons; like them, hover tint
   ;; only where hover exists (a tap's sticky :hover would leave it tinted).
   [sections/kebab-wrap
    [:button
     {:width "32px" :height "32px" :border-radius "999px"
      :color t/--note-kraft}
     [:&:hover {:background "transparent" :color t/--note-kraft}]]
    [:at-media {:hover "hover"}
     [:button [:&:hover {:background t/--note-kraft-10}]]]]]
  ;; Taller than the desktop card: a phone is the surface a note is most
  ;; likely to be written on, and the room it costs is a scroll down a column
  ;; rather than a hole in a dashboard grid.
  [notes-mini-app/list-body {:max-height "480px"}]
  [frame/list-clip
   {:border-bottom-left-radius "20px"
    :border-bottom-right-radius "20px"}]
  [frame/bottom-fade
   {:border-bottom-left-radius "20px"
    :border-bottom-right-radius "20px"}]
  [notes-mini-app/editor-pane {:height "360px"}])

;; The pane and the full-screen editor edit the one draft, so ⤢ only has to
;; open the editor and its ✕ only has to close it — what is written stays
;; where it is either way, and Cancel puts both back to the note as last
;; saved.
(defn- editing-view [{:keys [note]}]
  (r/with-let [!screen-open? (r/atom false)
               !pane-el (atom nil)
               ;; Leaving the full-screen editor puts the writer back in the
               ;; pane mid-sentence, so the text takes the focus rather than
               ;; the browser dropping it on the body. Nothing restores it
               ;; otherwise: the editor is an overlay, not a dialog.
               focus-text! (fn []
                             (when-let [^js el @!pane-el]
                               (some-> (.querySelector el "textarea.body") (.focus))))
               close-screen! (fn []
                               (reset! !screen-open? false)
                               (focus-text!))]
    (let [screen-open? @!screen-open?
          can-edit? (notes-mini-app/editable? note)
          editor-props {:note            note
                        :draft-id        recent/draft-id
                        :spaces?         @(<s [::spaces-data/enabled?])
                        :saving?         (= :start @(<s [:fetch/state ::recent/save]))
                        :on-save         #(>e [::recent/save % {:source :web-app :note-link note-link}])
                        :on-cancel       #(>e [::recent/cancel])
                        :on-open-entity  entity-data/open-entity
                        :on-share-closed #(>e [::recent/refresh-editing])}]
      [:<>
       [notes-mini-app/editing-header
        {:note            note
         :on-close        #(>e [::recent/close-editor])
         :on-expand       #(reset! !screen-open? true)
         :on-delete       #(>e [::recent/delete])
         :on-share-closed #(>e [::recent/refresh-editing])}]
       [notes-mini-app/editor-view
        (assoc editor-props
               :pane-ref #(reset! !pane-el %)
               :auto-focus? true)]
       (when screen-open?
         [editor/screen
          (assoc editor-props
                 :title (notes-mini-app/editor-title note)
                 :editing? can-edit?
                 :share? (sharing/can-share? note)
                 :on-close close-screen!
                 :on-delete (fn []
                              (close-screen!)
                              (>e [::recent/delete])))])])))

(defn panel [{title :mini-app/title}]
  (let [collapsed? @(<s [::mini-apps-data/collapsed? :notes])
        loaded? @(<s [::recent/loaded?])
        notes @(<s [::recent/list])
        editing-note @(<s [::recent/editing-note])]
    (if editing-note
      [card
       [editing-view {:note editing-note}]]
      [card {:class (when collapsed? "collapsed")}
       [notes-mini-app/list-header
        {:title               title
         :href                (routing/href :notes/view)
         :target              routing/link-target
         :collapsed?          collapsed?
         :on-toggle-collapsed #(>e [::mini-apps-data/toggle-collapsed :notes])
         :on-add              (fn []
                                (>e [::mini-apps-data/expand :notes])
                                (>e [::recent/create]))}]
       (when-not collapsed?
         (if loaded?
           [notes-mini-app/note-list
            {:notes          notes
             :on-open        #(>e [::recent/edit %])
             :on-open-entity entity-data/open-entity
             :see-all        {:href (routing/href :notes/view) :target routing/link-target}
             :empty-text     "Nothing yet — add your first note above."}]
           [notes-mini-app/list-loading]))])))
